import { observer } from "mobx-react-lite";
import type {
  ActionOutputData,
  RecordJobData,
} from "@smartsuite/automation-engine-api";
import { translateAction } from "../../../../../locales/i18n";
import { t } from "i18next";
import { ActionError } from "../ActionError/ActionError";
import { getErrorMessage } from "../../../ActivityGrid/ActivityGrid.helpers";
import { ActivityDetailsGroup } from "../ActivityDetailsGroup/ActivityDetailsGroup";
import { useRootStore } from "../../../../../stores/hooks";
import { useMemo, useState } from "react";
import { isOldTypejob } from "../../utils";
import { getActionRecordCount } from "../../../shared/status-walk";
import { useRecordJobData } from "../../hooks/useRecordJobData";
import { GeneralDeleteGroupWrapper } from "../DeleteRecordsGroupWrapper/DeleteRecordsGroupWrapper";

interface DeleteRecordsGroupProps {
  action: ActionOutputData;
  /** Top-level group the action came from; record-job paths are keyed by it. */
  groupInstanceId?: number;
  onDetailsClick?: () => void;
}

export const DeleteRecordsGroup = observer<DeleteRecordsGroupProps>(
  ({ action, groupInstanceId = 0, onDetailsClick }) => {
    const {
      integrations,
      jobDetails,
      config: { baseUrl },
    } = useRootStore();
    const [recordJobData, setRecordJobData] = useState<
      Record<string, RecordJobData>
    >({});
    const selectedIntegration = integrations.list.find(
      (i) => i.integrationId === action.actionReference?.integrationId,
    );
    const selectedAction = selectedIntegration?.actions.find(
      (a) => a.actionId === action.actionReference?.actionId,
    );
    const oldTypeJob = useMemo(
      () => isOldTypejob(jobDetails),
      [jobDetails.job],
    );
    const errorMessage = getErrorMessage(action, jobDetails.job);
    const status = action.status.oneofKind === "ok" ? "is_success" : "is_error";
    const filteredRecordJobDatas = jobDetails.recordJobData.filter(
      (recordJobData) => {
        return (
          recordJobData.outputData?.recordList?.recordList !== undefined &&
          recordJobData.outputData.recordList.recordList.records.length > 0 &&
          recordJobData.outputData.recordList.recordList.records[0]?.outputs.some(
            (output) => output.outputId === "record_id",
          ) &&
          recordJobData.actionReference?.actionId ===
            action.actionReference?.actionId &&
          recordJobData.actionReference?.instanceId ===
            action.actionReference?.instanceId &&
          recordJobData.actionReference?.integrationId ===
            action.actionReference?.integrationId
        );
      },
    );
    const { fetchRecordJobData } = useRecordJobData({
      recordJobData,
      setRecordJobData,
    });
    const onDetailsClickWithFetch = async (
      recordJodId: string,
    ): Promise<void> => {
      await fetchRecordJobData(recordJodId);
    };

    const recordListCount = action.recordListState?.recordFinishedCount;
    const recordCount = getActionRecordCount(action, jobDetails.job);

    return (
      <ActivityDetailsGroup
        label={
          selectedAction !== undefined ? translateAction(selectedAction) : ""
        }
        subLabel={t("job-list.records-found", {
          count: recordListCount,
        })}
        status={status}
      >
        {status === "is_error" && <ActionError errorMessage={errorMessage} />}
        {!oldTypeJob &&
          recordCount > 0 &&
          Array.from({ length: recordCount }).map((_, idx) => {
            const recordJodId = `jobs/${jobDetails.job.jobId}/group-${groupInstanceId}/action-${
              action.actionReference?.instanceId ?? ""
            }/record-${idx}`;
            return (
              <GeneralDeleteGroupWrapper
                key={idx}
                index={idx}
                recordJob={recordJobData[recordJodId]}
                onDetailsClickWithFetch={onDetailsClickWithFetch}
                recordJodId={recordJodId}
              />
            );
          })}
        {oldTypeJob &&
          filteredRecordJobDatas.map((recordJob, index) => {
            const recordJodId = `jobs/${jobDetails.job.jobId}/group-0/action-${
              action.actionReference?.instanceId ?? ""
            }/record-${index}`;
            return (
              <GeneralDeleteGroupWrapper
                key={recordJodId}
                index={index}
                recordJob={recordJob}
                onDetailsClick={onDetailsClick}
              />
            );
          })}
      </ActivityDetailsGroup>
    );
  },
);
